import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { Comment as MemberComment } from "../types/Comment";
import type { Member, MemberLoginResponse, MemberSort } from "../types/Member";
import type { MessageableEntity } from "../types/MessageableEntity";
import type { PaginatedResponse } from "../types/Pagination";
import type { DeletionPreview, PermanentDeletionResponse } from "../types/MemberDeletion";
import type {
  IMemberAPI,
  SuspendMemberResponse,
  RevokeSuspensionResponse,
  TerminateMemberResponse,
  RevokeTerminationResponse,
  ReactivateMemberResponse,
  AcceptTermsResponse,
  EndMembershipResponse,
  RevokeEndMembershipResponse,
} from "./memberApi";

class RemoteMemberAPI implements IMemberAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async loginMember(): Promise<MemberLoginResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const data: MemberLoginResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", "Failed to login member", error);
      return Error("Failed to login member");
    }
  }

  async fetchMember(): Promise<Member | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      const member: Member = json.member;
      return member;
    } catch (error) {
      log("remoteMemberApi", "Failed to fetch member", error);
      throw error;
    }
  }

  async fetchMemberById(id: string): Promise<Member | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.statusText}`);
      }

      const json = await response.json();
      const member: Member = json.data;
      return member;
    } catch (error) {
      log("remoteMemberApi", "Failed to fetch member", error);
      throw error;
    }
  }

  async fetchMembersByIds(memberIds: string[]): Promise<{ members: Member[]; missingIds: string[] }> {
    if (!Array.isArray(memberIds) || memberIds.length === 0) {
      return { members: [], missingIds: [] };
    }
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/by-ids`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ memberIds }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const json = await response.json();
      const members: Member[] = json.members ?? [];
      const missingIds: string[] = json.missingIds ?? [];
      return { members, missingIds };
    } catch (error) {
      log("remoteMemberApi", "Failed to bulk-fetch members", error);
      throw error;
    }
  }

  async fetchSerializedMembers(
    searchTerm?: string,
    skip?: number,
    limit?: number,
    permissableLocationIds?: string,
    status?: { active?: boolean; isTerminated?: boolean; isPendingTermination?: boolean },
    sort?: MemberSort,
    excludeUnassignable?: boolean,
  ): Promise<PaginatedResponse<Member>> {
    try {
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        params.append("searchTerm", searchTerm.trim());
      }
      if (permissableLocationIds && permissableLocationIds.trim()) {
        params.append("permissableLocationIds", permissableLocationIds.trim());
      }
      if (skip !== undefined) {
        params.append("skip", skip.toString());
      }
      if (limit !== undefined) {
        params.append("limit", limit.toString());
      }
      // Member-status filters — forwarded server-side so pagination counts stay correct.
      if (status?.active !== undefined) {
        params.append("active", String(status.active));
      }
      if (status?.isTerminated) {
        params.append("isTerminated", "true");
      }
      if (status?.isPendingTermination) {
        params.append("isPendingTermination", "true");
      }
      // Sorting is server-side: it must span the whole result set, not the current page.
      if (sort) {
        params.append("sortField", sort.field);
        params.append("sortOrder", sort.order);
      }
      // Hides members flagged canBeAssignedBookings=false from non-admin callers (e.g. the
      // elevated-user on-behalf booking search), so restricted members never appear there.
      if (excludeUnassignable) {
        params.append("excludeUnassignable", "true");
      }

      const url = params.toString()
        ? `${API_BASE_URL}/api/members?${params.toString()}`
        : `${API_BASE_URL}/api/members`;

      const response = await fetchWithAuth(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result: PaginatedResponse<Member> = await response.json();
      return result;
    } catch (error) {
      log("remoteMemberApi", "Failed to fetch members", error);
      throw error;
    }
  }

  async exportMembers(
    searchTerm?: string,
    permissableLocationIds?: string,
    status?: { active?: boolean; isTerminated?: boolean; isPendingTermination?: boolean },
  ): Promise<Member[]> {
    try {
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        params.append("searchTerm", searchTerm.trim());
      }
      if (permissableLocationIds && permissableLocationIds.trim()) {
        params.append("permissableLocationIds", permissableLocationIds.trim());
      }
      if (status?.active !== undefined) {
        params.append("active", String(status.active));
      }
      if (status?.isTerminated) {
        params.append("isTerminated", "true");
      }
      if (status?.isPendingTermination) {
        params.append("isPendingTermination", "true");
      }

      const url = params.toString()
        ? `${API_BASE_URL}/api/members/export?${params.toString()}`
        : `${API_BASE_URL}/api/members/export`;

      const response = await fetchWithAuth(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result: { data: Member[] } = await response.json();
      return result.data ?? [];
    } catch (error) {
      log("remoteMemberApi", "Failed to export members", error);
      throw error;
    }
  }

  async fetchMembersOfPractice(
    permissableLocationIds: string[],
    searchTerm?: string,
    skip?: number,
    limit?: number,
    status?: { active?: boolean; isTerminated?: boolean; isPendingTermination?: boolean },
    sort?: MemberSort,
  ): Promise<PaginatedResponse<Member>> {
    try {
      const params = new URLSearchParams();
      params.append("permissableLocationIds", permissableLocationIds.join(","));
      if (searchTerm && searchTerm.trim()) {
        params.append("searchTerm", searchTerm.trim());
      }
      if (skip !== undefined) {
        params.append("skip", skip.toString());
      }
      if (limit !== undefined) {
        params.append("limit", limit.toString());
      }
      // Status filters applied server-side so they respect pagination (same /api/members endpoint
      // the Admin list uses). See MemberFilters / memberStatusToFilters.
      if (status?.active !== undefined) {
        params.append("active", String(status.active));
      }
      if (status?.isTerminated) {
        params.append("isTerminated", "true");
      }
      if (status?.isPendingTermination) {
        params.append("isPendingTermination", "true");
      }
      // Sorting is server-side: it must span the whole result set, not the current page.
      if (sort) {
        params.append("sortField", sort.field);
        params.append("sortOrder", sort.order);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/api/members?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result: PaginatedResponse<Member> = await response.json();
      return result;
    } catch (error) {
      log("remoteMemberApi", "Failed to fetch members of practice", error);
      throw error;
    }
  }

  async addMemberToLocation(
    memberId: string,
    locationId: string,
  ): Promise<{ message: string; success: boolean } | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ memberId }),
      });
      if (!response.ok) {
        // Surface the server's specific message (e.g. "This member is already assigned to this
        // location") so the invite UI can tell the PM what went wrong instead of failing silently.
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }
      const data = await response.json();
      return { message: data.message, success: data.success };
    } catch (error) {
      log("remoteMemberApi", "Failed to add member to location", error);
      throw error;
    }
  }

  async updateMember(memberId: string, updates: Partial<Member>): Promise<Member | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.statusText}`);
      }

      const updatedMember: Member = await response.json();
      return updatedMember;
    } catch (error) {
      log("remoteMemberApi", `Failed to update member ID: ${memberId}`, error);
      return Error(`Failed to update member ID: ${memberId}`);
    }
  }

  async addCommentToMember(memberId: string, comment: string): Promise<MemberComment[] | Error | []> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/members/${memberId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ comment }),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const comments: MemberComment[] = await response.json();
      return comments;
    } catch (error) {
      log("remoteMemberApi", `Failed to add comment to member ID: ${memberId}`, error);
      return Error(`Failed to add comment to member ID: ${memberId}`);
    }
  }

  async fetchMemberComments(memberId: string): Promise<MemberComment[] | Error | []> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/members/${memberId}/comments`);
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const comments: MemberComment[] = await response.json();
      return comments;
    } catch (error) {
      log("remoteMemberApi", `Failed to fetch comments for member ID: ${memberId}`, error);
      return Error(`Failed to fetch comments for member ID: ${memberId}`);
    }
  }

  async fetchMembersICanMessage(): Promise<Array<MessageableEntity>> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/can-message`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const entities: MessageableEntity[] = await response.json();
      return entities;
    } catch (error) {
      log("remoteMemberApi", "Failed to fetch members I can message", error);
      throw error;
    }
  }

  async fetchMembersByLocation(locationId: string, searchTerm?: string): Promise<Member[]> {
    try {
      const params = new URLSearchParams({
        permissableLocationIds: locationId,
      });

      if (searchTerm && searchTerm.trim()) {
        params.append("searchTerm", searchTerm.trim());
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/api/members?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: PaginatedResponse<Member> = await response.json();
      return result.data;
    } catch (error) {
      log("remoteMemberApi", "Failed to fetch members by location", error);
      throw error;
    }
  }

  // Terms acceptance
  async acceptTerms(memberId: string, termsVersion: number): Promise<AcceptTermsResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ termsVersion }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.statusText}`);
      }

      const updatedMember: Member = await response.json();
      return { success: true, member: updatedMember };
    } catch (error) {
      log("remoteMemberApi", `Failed to accept terms for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to accept terms for member ID: ${memberId}`);
    }
  }

  // Annual declaration acceptance
  async acceptAnnualDeclaration(memberId: string): Promise<AcceptTermsResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/accept-annual-declaration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.details || `API error: ${response.statusText}`);
      }

      const result = await response.json();
      return { success: true, member: result.member as Member };
    } catch (error) {
      log("remoteMemberApi", `Failed to accept annual declaration for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to accept annual declaration for member ID: ${memberId}`);
    }
  }

  // Suspension endpoints
  async suspendMember(memberId: string, suspensionEndDate: string): Promise<SuspendMemberResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/suspend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ suspensionEndDate }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: SuspendMemberResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to suspend member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to suspend member ID: ${memberId}`);
    }
  }

  async revokeSuspension(memberId: string): Promise<RevokeSuspensionResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/suspend`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: RevokeSuspensionResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to revoke suspension for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to revoke suspension for member ID: ${memberId}`);
    }
  }

  // Termination endpoints
  async terminateMember(memberId: string, terminationDate: string): Promise<TerminateMemberResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/terminate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ terminationDate }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: TerminateMemberResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to terminate member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to terminate member ID: ${memberId}`);
    }
  }

  async revokeTermination(memberId: string): Promise<RevokeTerminationResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/terminate`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: RevokeTerminationResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to revoke termination for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to revoke termination for member ID: ${memberId}`);
    }
  }

  // End Membership — unified flow replacing standalone Suspend + Terminate.
  async endMembership(memberId: string, endDate?: string): Promise<EndMembershipResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/end-membership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(endDate ? { endDate } : {}),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.details || errorData.error || `API error: ${response.statusText}`);
      }
      return (await response.json()) as EndMembershipResponse;
    } catch (error) {
      log("remoteMemberApi", `Failed to end membership for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to end membership for member ID: ${memberId}`);
    }
  }

  async revokeEndMembership(memberId: string): Promise<RevokeEndMembershipResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/end-membership`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }
      return (await response.json()) as RevokeEndMembershipResponse;
    } catch (error) {
      log("remoteMemberApi", `Failed to revoke end-membership for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to revoke end-membership for member ID: ${memberId}`);
    }
  }

  async reactivateMember(memberId: string): Promise<ReactivateMemberResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/reactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: ReactivateMemberResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to reactivate member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to reactivate member ID: ${memberId}`);
    }
  }

  // Permanent deletion endpoints
  async previewPermanentDeletion(memberId: string): Promise<DeletionPreview | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/deletion-preview`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: DeletionPreview = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to preview deletion for member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to preview deletion for member ID: ${memberId}`);
    }
  }

  async permanentlyDeleteMember(
    memberId: string,
    confirmText: string,
    reason?: string
  ): Promise<PermanentDeletionResponse | Error> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/permanent`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ confirmText, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `API error: ${response.statusText}`);
      }

      const data: PermanentDeletionResponse = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to permanently delete member ID: ${memberId}`, error);
      return error instanceof Error ? error : new Error(`Failed to permanently delete member ID: ${memberId}`);
    }
  }

  // SENSITIVE, AUDITED, ON-DEMAND: only ever called from an explicit user click (see
  // useLastLoginCode / LastLoginCodePanel) — every hit is recorded server-side.
  async getLastLoginCode(memberId: string): Promise<{ code: string; expiresAt: number } | { code: null }> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/members/${memberId}/last-login-code`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("You don't have permission to reveal this member's login code.");
        }
        if (response.status === 404) {
          throw new Error("Member not found.");
        }
        if (response.status === 429) {
          throw new Error("Too many attempts — please try again shortly.");
        }
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      log("remoteMemberApi", `Failed to reveal last login code for member ID: ${memberId}`, error);
      throw error instanceof Error ? error : new Error("Couldn't reveal the login code. Please try again.");
    }
  }
}

export default RemoteMemberAPI;
